import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { RestockOrderPage } from 'src/pages/inventory/restock-order/restock-order.page';

import { formatMoney, stateLabel } from 'src/shared/services/restock-order-state.pipe';

import {
  RestockingOrdersService,
  RestockOrder
} from 'src/shared/services/restocking-orders.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

interface OrderCardVM {
  id: number;
  orderTitle: string;
  chipClass: string;
  chipLabel: string;
  summaryText: string;   // '3 items · $89.95 · Beach House'
  dateText: string;      // 'Submitted Jul 9, 2026'
  extraText: string;     // 'Arrives ~Jul 13' | 'Delivered Jun 27' | ''
}

function fmtDate(value: string): string {
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtDateWithYear(value: string): string {
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

@Component({
  selector: 'restock-order-history-page',
  templateUrl: 'restock-order-history.page.html',
  standalone: true,
  imports: [CommonModule, IonicModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class RestockOrderHistoryPage implements OnInit {
  private readonly navCtrl = inject(CustomNavController);
  private readonly storage = inject(TidyStorage);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly restockingService = inject(RestockingOrdersService);
  private readonly util = inject(Util);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly windowService = inject(WindowService);

  loaded = false;
  isRightSideContent = false;
  orders: OrderCardVM[] = [];

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('Order History');
    }
    this.restockingService.orders$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((orders) => {
        this.orders = orders.map((order) => this.toCardVM(order));
        this.loaded = true;
        this.cdr.markForCheck();
      });
    try {
      await this.restockingService.loadOrders();
    } catch {
      this.util.showError('Unable to load your order history. Please try again.');
      this.loaded = true;
      this.cdr.markForCheck();
    }
  }

  private toCardVM(order: RestockOrder): OrderCardVM {
    const total = order.finalTotal !== undefined && order.finalTotal !== null ? order.finalTotal : order.estimatedTotal;
    const itemCount = order.items.length;
    let extraText = '';
    if (order.state === 'ordered' && order.eta) {
      extraText = `Arrives ~${fmtDate(order.eta)}`;
    } else if (order.state === 'delivered') {
      const delivered = order.timeline.find((event) => event.label === 'Delivered');
      extraText = delivered ? `Delivered ${fmtDate(delivered.at)}` : '';
    }
    return {
      id: order.id,
      orderTitle: `Order #${order.id}`,
      chipClass: `restock-status-chip ${order.state}`,
      chipLabel: stateLabel(order.state),
      summaryText: `${itemCount} ${itemCount === 1 ? 'item' : 'items'} · ${formatMoney(total)} · ${order.propertyName}`,
      dateText: `Submitted ${fmtDateWithYear(order.createdAt)}`,
      extraText
    };
  }

  goToOrderDetail(orderId: number) {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo(`restock-order/${  orderId}`, { orderId }, RestockOrderPage);
    } else {
      this.navCtrl.navigateForward(`restock-order/${  orderId}`, { orderId });
    }
  }
}
